import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import {
  AvailableQuestion,
  LinkedQuestion,
  OnlineExamsApi,
  OnlineExamStatus,
} from '@/services/school.api';
import { AcademicClass, AcademicClassesApi, AcademicYearsApi, ClassSubjectRow } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input, Select } from '@/components/ui/Input';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/cn';

const STATUS_TONE: Record<OnlineExamStatus, 'slate' | 'purple'> = {
  draft: 'slate',
  published: 'purple',
};

/** One entry per distinct subject taught to the class — same dedupe pattern as ExamsPage.tsx. */
function dedupeBySubject(rows: ClassSubjectRow[]): ClassSubjectRow[] {
  const seen = new Map<string, ClassSubjectRow>();
  for (const r of rows) if (!seen.has(r.subjectId)) seen.set(r.subjectId, r);
  return [...seen.values()];
}

export function OnlineExamsPage() {
  const [openExamId, setOpenExamId] = useState<string | null>(null);
  if (openExamId) {
    return <OnlineExamWorkspace examId={openExamId} onBack={() => setOpenExamId(null)} />;
  }
  return <OnlineExamsList onOpen={setOpenExamId} />;
}

// ───── List + create ──────────────────────────────────────────────────────────
function OnlineExamsList({ onOpen }: { onOpen: (id: string) => void }) {
  const { can } = usePermissions();
  const canWrite = can('/online-exams', 'create');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [classSubjectId, setClassSubjectId] = useState('');
  const [academicYearId, setAcademicYearId] = useState('');
  const [creating, setCreating] = useState(false);

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'online-exams'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const selectedClass = classes.find((c) => c.id === classId);

  const { data: academicYearsPage } = useQuery({
    queryKey: ['academic-years', 'online-exams'],
    queryFn: () => AcademicYearsApi.list({ limit: 100 }),
  });
  const academicYears = academicYearsPage?.items ?? [];

  const { data: subjectRows = [] } = useQuery({
    queryKey: ['class-subjects', 'online-exams-filter', classId],
    queryFn: () => AcademicClassesApi.subjects(classId),
    enabled: !!classId,
  });
  const subjects = dedupeBySubject(subjectRows);

  const { data: exams = [], isLoading } = useQuery({
    queryKey: ['online-exams', sectionId, classSubjectId, academicYearId],
    queryFn: () =>
      OnlineExamsApi.list({
        classSectionId: sectionId || undefined,
        classSubjectId: classSubjectId || undefined,
        academicYearId: academicYearId || undefined,
      }),
  });

  return (
    <>
      <PageHeader
        title="Online Exams"
        description="Timed, MCQ-based online exams. Fully separate from offline Exams & Marks — no shared data, no merged results."
        actions={
          canWrite && (
            <button className="btn-primary" onClick={() => setCreating(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> New online exam
            </button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select
          className="!w-48"
          value={classId}
          onChange={(e) => {
            setClassId(e.target.value);
            setSectionId('');
            setClassSubjectId('');
          }}
        >
          <option value="">All classes</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </Select>
        <Select className="!w-40" value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId}>
          <option value="">All sections</option>
          {(selectedClass?.sections ?? []).map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </Select>
        <Select
          className="!w-44"
          value={classSubjectId}
          onChange={(e) => setClassSubjectId(e.target.value)}
          disabled={!classId}
        >
          <option value="">All subjects</option>
          {subjects.map((s) => (
            <option key={s.classSubjectId} value={s.classSubjectId}>{s.subjectName}</option>
          ))}
        </Select>
        <Select className="!w-40" value={academicYearId} onChange={(e) => setAcademicYearId(e.target.value)}>
          <option value="">All session years</option>
          {academicYears.map((y) => (
            <option key={y.id} value={y.id}>{y.name}</option>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : exams.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">
          No online exams yet. {canWrite && (
            <>
              Click <span className="font-medium">New online exam</span> to create one.
            </>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <Th>Exam</Th>
                <Th>Class · Section · Subject</Th>
                <Th>Window</Th>
                <Th>Status</Th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {exams.map((ex) => (
                <tr key={ex.id} onClick={() => onOpen(ex.id)} className="cursor-pointer hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{ex.name}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">
                    {ex.className ?? '—'} · {ex.sectionName ?? '—'} · {ex.subjectName ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">
                    {new Date(ex.startDatetime).toLocaleString()} → {new Date(ex.endDatetime).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={STATUS_TONE[ex.status]}>{ex.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <ChevronRight className="inline h-4 w-4 text-slate-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CreateOnlineExamModal classes={classes} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); onOpen(id); }} />}
    </>
  );
}

const createSchema = z.object({
  name: z.string().min(1, 'Required'),
  classId: z.string().min(1, 'Required'),
  sectionId: z.string().min(1, 'Required'),
  classSubjectId: z.string().min(1, 'Required'),
  durationMinutes: z.coerce.number().min(1, 'Must be at least 1 minute'),
  startDatetime: z.string().min(1, 'Required'),
  endDatetime: z.string().min(1, 'Required'),
});
type CreateForm = z.infer<typeof createSchema>;

function CreateOnlineExamModal({
  classes,
  onClose,
  onCreated,
}: {
  classes: AcademicClass[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CreateForm>({ resolver: zodResolver(createSchema) });
  const classId = watch('classId');
  const selectedClass = classes.find((c) => c.id === classId);

  const { data: subjectRows = [] } = useQuery({
    queryKey: ['class-subjects', classId],
    queryFn: () => AcademicClassesApi.subjects(classId),
    enabled: !!classId,
  });
  const subjects = dedupeBySubject(subjectRows);

  const create = useMutation({
    mutationFn: (f: CreateForm) =>
      OnlineExamsApi.create({
        name: f.name,
        classSectionId: f.sectionId,
        classSubjectId: f.classSubjectId,
        durationMinutes: f.durationMinutes,
        startDatetime: new Date(f.startDatetime).toISOString(),
        endDatetime: new Date(f.endDatetime).toISOString(),
      }),
    onSuccess: (exam) => {
      qc.invalidateQueries({ queryKey: ['online-exams'] });
      onCreated(exam.id);
    },
  });

  return (
    <Modal open onClose={onClose} title="New online exam" size="md">
      <form onSubmit={handleSubmit((f) => create.mutate(f))} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Exam name</label>
          <Input placeholder="e.g. Algebra Basics — Quiz 1" {...register('name')} />
          {errors.name && <FieldErr msg={errors.name.message} />}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Class</label>
            <Select {...register('classId')}>
              <option value="">Select class</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
            {errors.classId && <FieldErr msg={errors.classId.message} />}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Section</label>
            <Select {...register('sectionId')} disabled={!classId}>
              <option value="">{classId ? 'Select section' : 'Pick a class first'}</option>
              {(selectedClass?.sections ?? []).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </Select>
            {errors.sectionId && <FieldErr msg={errors.sectionId.message} />}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Subject</label>
            <Select {...register('classSubjectId')} disabled={!classId}>
              <option value="">{classId ? 'Select subject' : 'Pick a class first'}</option>
              {subjects.map((s) => (
                <option key={s.classSubjectId} value={s.classSubjectId}>{s.subjectName}</option>
              ))}
            </Select>
            {errors.classSubjectId && <FieldErr msg={errors.classSubjectId.message} />}
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Duration (minutes)</label>
          <Input type="number" min={1} placeholder="30" {...register('durationMinutes')} />
          {errors.durationMinutes && <FieldErr msg={errors.durationMinutes.message} />}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Opens</label>
            <Input type="datetime-local" {...register('startDatetime')} />
            {errors.startDatetime && <FieldErr msg={errors.startDatetime.message} />}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Closes</label>
            <Input type="datetime-local" {...register('endDatetime')} />
            {errors.endDatetime && <FieldErr msg={errors.endDatetime.message} />}
          </div>
        </div>
        {create.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(create.error)}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create & link questions'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ───── Workspace (per exam) ────────────────────────────────────────────────────
type Tab = 'questions' | 'results';

function OnlineExamWorkspace({ examId, onBack }: { examId: string; onBack: () => void }) {
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can('/online-exams', 'create');
  const [tab, setTab] = useState<Tab>('questions');
  const [publishError, setPublishError] = useState<string | null>(null);

  const { data: exam } = useQuery({ queryKey: ['online-exam', examId], queryFn: () => OnlineExamsApi.get(examId) });
  const { data: linkedQuestions = [] } = useQuery({
    queryKey: ['online-exam-linked-questions', examId],
    queryFn: () => OnlineExamsApi.listLinkedQuestions(examId),
  });

  const publish = useMutation({
    mutationFn: () => OnlineExamsApi.publish(examId),
    onSuccess: () => {
      setPublishError(null);
      qc.invalidateQueries({ queryKey: ['online-exam', examId] });
      qc.invalidateQueries({ queryKey: ['online-exams'] });
    },
    onError: (e) => setPublishError(invalidQuestionsMsg(e)),
  });

  const isDraft = exam?.status === 'draft';

  return (
    <>
      <PageHeader
        title={exam?.name ?? 'Online exam'}
        description={
          exam ? (
            <span className="inline-flex items-center gap-2">
              <Badge tone={STATUS_TONE[exam.status]}>{exam.status}</Badge>
              {exam.className} · {exam.sectionName} · {exam.subjectName} · {exam.durationMinutes} min
            </span>
          ) : ' '
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button className="btn-secondary" onClick={onBack}>
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
            </button>
            {exam && canWrite && isDraft && (
              <button className="btn-primary" onClick={() => publish.mutate()} disabled={publish.isPending || linkedQuestions.length === 0}>
                {publish.isPending ? 'Publishing…' : 'Publish'}
              </button>
            )}
          </div>
        }
      />

      {publishError && (
        <div className="mb-4 whitespace-pre-line rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {publishError}
        </div>
      )}

      <div className="mb-4 flex gap-1 border-b border-slate-200">
        {(['questions', 'results'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'border-b-2 px-3 py-2 text-sm font-medium capitalize',
              tab === t ? 'border-brand-500 text-brand-600' : 'border-transparent text-slate-500 hover:text-slate-700',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {exam && tab === 'questions' && (
        <LinkedQuestionsTab examId={examId} questions={linkedQuestions} isDraft={isDraft} canWrite={canWrite} />
      )}
      {tab === 'results' && <ResultsTab examId={examId} />}
    </>
  );
}

function invalidQuestionsMsg(e: unknown): string {
  const anyE = e as any;
  const err = anyE?.response?.data?.error;
  if (err?.code === 'INVALID_QUESTIONS' && Array.isArray(err?.details)) {
    const details = err.details as { questionText: string; reason: string }[];
    return [err.message, ...details.map((d) => `• ${d.questionText}: ${d.reason}`)].join('\n');
  }
  return errMsg(e) ?? 'Failed to publish';
}

// ───── Linked questions tab ─────────────────────────────────────────────────────
function LinkedQuestionsTab({
  examId,
  questions,
  isDraft,
  canWrite,
}: {
  examId: string;
  questions: LinkedQuestion[];
  isDraft: boolean;
  canWrite: boolean;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['online-exam-linked-questions', examId] });

  const updateMarks = useMutation({
    mutationFn: ({ questionId, marks }: { questionId: string; marks: number }) => OnlineExamsApi.updateLinkMarks(examId, questionId, marks),
    onSuccess: invalidate,
  });
  const unlink = useMutation({
    mutationFn: (questionId: string) => OnlineExamsApi.unlinkQuestion(examId, questionId),
    onSuccess: invalidate,
  });

  const canEdit = canWrite && isDraft;

  return (
    <div className="space-y-4">
      {!isDraft && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-600">
          This exam is published — linked questions are locked. Structure can only change while a draft.
        </div>
      )}
      {questions.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">
          No questions linked yet. {canEdit && (
            <>
              Click <span className="font-medium">Add from bank</span> below.
            </>
          )}
        </div>
      ) : (
        questions.map((q, i) => (
          <div key={q.questionId} className="card p-4">
            <div className="mb-2 flex items-start justify-between gap-2">
              <div className="text-sm font-medium text-slate-900">{i + 1}. {q.questionText}</div>
              {canEdit && (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    step="0.5"
                    defaultValue={q.marks}
                    className="!w-20 !py-1 text-sm"
                    onBlur={(e) => {
                      const marks = Number(e.target.value);
                      if (!Number.isNaN(marks) && marks !== q.marks) updateMarks.mutate({ questionId: q.questionId, marks });
                    }}
                  />
                  <button
                    className="rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    title="Unlink — does not delete the bank question"
                    onClick={() => unlink.mutate(q.questionId)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
              {!canEdit && <span className="text-sm text-slate-500">{q.marks} {q.marks === 1 ? 'mark' : 'marks'}</span>}
            </div>
            <ul className="space-y-1 text-sm text-slate-500">
              {q.options.map((o) => (
                <li key={o.id}>{o.optionText}</li>
              ))}
            </ul>
          </div>
        ))
      )}
      {canEdit && (
        <button className="btn-secondary" onClick={() => setAdding(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> Add from bank
        </button>
      )}
      {adding && <AddFromBankModal examId={examId} onClose={() => setAdding(false)} onLinked={invalidate} />}
    </div>
  );
}

function AddFromBankModal({ examId, onClose, onLinked }: { examId: string; onClose: () => void; onLinked: () => void }) {
  const { data: available = [], isLoading } = useQuery({
    queryKey: ['online-exam-available-questions', examId],
    queryFn: () => OnlineExamsApi.availableQuestions(examId),
  });
  const [selected, setSelected] = useState<Record<string, number>>({});

  const link = useMutation({
    mutationFn: () =>
      OnlineExamsApi.linkQuestions(
        examId,
        Object.entries(selected).map(([questionId, marks]) => ({ questionId, marks })),
      ),
    onSuccess: () => {
      onLinked();
      onClose();
    },
  });

  const toggle = (q: AvailableQuestion) =>
    setSelected((s) => {
      const next = { ...s };
      if (q.id in next) delete next[q.id];
      else next[q.id] = 1;
      return next;
    });

  const selectedCount = Object.keys(selected).length;

  return (
    <Modal open onClose={onClose} title="Add questions from bank" size="lg">
      <div className="space-y-3">
        {isLoading ? (
          <div className="p-6 text-center text-slate-400">Loading…</div>
        ) : available.length === 0 ? (
          <div className="p-6 text-center text-slate-500">
            No unlinked bank questions match this exam's class section + subject. Author more in the Question Bank first.
          </div>
        ) : (
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {available.map((q) => {
              const isSelected = q.id in selected;
              return (
                <div key={q.id} className={cn('rounded-md border p-3', isSelected ? 'border-brand-500 bg-brand-50' : 'border-slate-200')}>
                  <label className="flex items-start gap-2">
                    <input type="checkbox" className="mt-1 h-4 w-4" checked={isSelected} onChange={() => toggle(q)} />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-900">{q.questionText}</div>
                      <div className="mt-0.5 text-xs text-slate-500">{q.options.length} options</div>
                    </div>
                    {isSelected && (
                      <Input
                        type="number"
                        min={0}
                        step="0.5"
                        value={selected[q.id]}
                        onChange={(e) => setSelected((s) => ({ ...s, [q.id]: Number(e.target.value) }))}
                        className="!w-20 !py-1 text-sm"
                        placeholder="Marks"
                      />
                    )}
                  </label>
                </div>
              );
            })}
          </div>
        )}
        {link.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(link.error)}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" disabled={selectedCount === 0 || link.isPending} onClick={() => link.mutate()}>
            {link.isPending ? 'Linking…' : `Link ${selectedCount || ''} question${selectedCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ───── Results tab ──────────────────────────────────────────────────────────────
function ResultsTab({ examId }: { examId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['online-exam-results', examId],
    queryFn: () => OnlineExamsApi.results(examId),
  });

  if (isLoading) return <div className="card p-8 text-center text-slate-400">Loading…</div>;
  const students = data?.students ?? [];
  if (students.length === 0) return <div className="card p-8 text-center text-slate-500">No students enrolled in this exam's section.</div>;

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50">
          <tr>
            <Th>Student</Th>
            <Th>Status</Th>
            <Th>Started</Th>
            <Th>Submitted</Th>
            <Th>Score</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {students.map((r) => (
            <tr key={r.studentId}>
              <td className="px-4 py-3 text-sm font-medium text-slate-900">{r.studentName}</td>
              <td className="px-4 py-3">
                <Badge tone={r.status === 'submitted' ? 'green' : r.status === 'in_progress' ? 'amber' : 'slate'}>
                  {r.status.replace('_', ' ')}
                </Badge>
              </td>
              <td className="px-4 py-3 text-sm text-slate-500">{r.startedAt ? new Date(r.startedAt).toLocaleString() : '—'}</td>
              <td className="px-4 py-3 text-sm text-slate-500">{r.submittedAt ? new Date(r.submittedAt).toLocaleString() : '—'}</td>
              <td className="px-4 py-3 text-sm text-slate-600">
                {r.score === null ? '—' : `${r.score} / ${r.totalMarks}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ───── helpers ────────────────────────────────────────────────────────────────
function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{children}</th>
  );
}

function FieldErr({ msg }: { msg?: string }) {
  return <p className="mt-1 text-xs text-red-600">{msg}</p>;
}

function errMsg(e: unknown): string | undefined {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
